<script setup lang="ts">
import { watchImmediate } from "@vueuse/core"
import createGlobe from "cobe"
import { onMounted, ref } from "vue"
import { useSpring } from "vue-use-spring"

const { size } = defineProps<{
	size: number
}>()

let globeRenderer: ReturnType<typeof createGlobe>
const canvasRef = ref<HTMLCanvasElement | null>(null)
const pointerInteracting = ref<number | null>(null)
const pointerInteractionMovement = ref(0)
const phi = ref(0)

const api = useSpring(
	{ r: 0 },
	{
		mass: 1,
		tension: 280,
		friction: 40,
		precision: 0.001,
	},
)

onMounted(() => {
	watchImmediate(() => size, () => {
		destroy()
		render()
	})
})

onUnmounted(() => {
	destroy()
})

function destroy() {
	globeRenderer?.destroy()
}

function render() {
	globeRenderer = createGlobe(canvasRef.value!, {
		devicePixelRatio: 2,
		width: size * 2,
		height: size * 2,
		phi: 0,
		theta: 0,
		dark: 0,
		diffuse: 1.75,
		mapSamples: 42000,
		mapBrightness: 12,
		opacity: 1,
		baseColor: [1, 1, 1],
		markerColor: [0.1, 0.8, 1],
		glowColor: [1, 1, 1],
		markers: [],
		onRender: (state) => {
			// This prevents rotation while dragging
			if (!pointerInteracting.value) {
				// Called on every animation frame.
				// `state` will be an empty object, return updated params.
				phi.value += 0.005
			}
			state.phi = phi.value + api.r
		},
	})
	canvasRef.value!.style.opacity = "1"
}

function handlePointerDown(e: PointerEvent) {
	pointerInteracting.value = e.clientX - pointerInteractionMovement.value
	canvasRef.value!.style.cursor = "grabbing"
}

function handlePointerUp(_: PointerEvent) {
	pointerInteracting.value = null
	canvasRef.value!.style.cursor = "grab"
}

function handlePointerOut(_: PointerEvent) {
	pointerInteracting.value = null
	canvasRef.value!.style.cursor = "grab"
}

function handleMouseMove(e: MouseEvent) {
	if (pointerInteracting.value !== null) {
		const delta = e.clientX - pointerInteracting.value
		pointerInteractionMovement.value = delta
		api.r = delta / 200
	}
}

function handleTouchMove(e: TouchEvent) {
	if (pointerInteracting.value !== null && e.touches[0]) {
		const delta = e.touches[0].clientX - pointerInteracting.value
		pointerInteractionMovement.value = delta
		api.r = delta / 100
	}
}
</script>

<template>
	<div class="w-full flex items-center justify-center">
		<canvas
			ref="canvasRef"
			class="[contain:layout_paint_size] cursor-grab opacity-0 transition-opacity duration-500"
			:style="{
				width: `${size}px`,
				height: `${size}px`,
			}"
			@pointerdown="handlePointerDown"
			@pointerup="handlePointerUp"
			@pointerout="handlePointerOut"
			@mousemove="handleMouseMove"
			@touchmove="handleTouchMove" />
	</div>
</template>
